import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Upload,
    CloudUpload,
    Trash2,
    FolderOpen,
    Loader2,
} from "lucide-react";
import { cn, generateId, formatFileSize } from "../../lib/utils";
import { FileItem } from "./FileItem";
import { StatusBanner } from "./StatusBanner";
import {
    UploadFile,
    UploadResult,
    ExcelUploaderProps,
    UploadStatus,
} from "./upload.types";

const ACCEPTED_TYPES = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
];

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];

export const ExcelUploader: React.FC<ExcelUploaderProps> = ({
    uploadUrl = "/api/upload",
    maxFileSize = 10 * 1024 * 1024,
    maxFiles = 10,
    onUploadComplete,
    onFilesChange,
    className,
    disabled = false,
    userType = "generator",
    title,
    externalFiles,
    setExternalFiles,
    hideUploadButton = false,
    isParentUploading = false
}) => {
    // Internal state (used if NOT controlled by parent)
    const [internalFiles, setInternalFiles] = React.useState<UploadFile[]>([]);
    
    // Determine which state to use
    const files = externalFiles || internalFiles;
    const setFiles = setExternalFiles || setInternalFiles;

    const [dragActive, setDragActive] = React.useState(false);
    
    // Internal uploading state for Single Mode
    const [internalIsUploading, setInternalIsUploading] = React.useState(false);
    
    // Combined uploading state
    const isUploading = isParentUploading || internalIsUploading;

    const [uploadStatus, setUploadStatus] = React.useState<UploadStatus>("idle");
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Computed stats
    const pendingCount = files.filter((f) => f.status === "pending").length;
    const successCount = files.filter((f) => f.status === "success").length;
    const errorCount = files.filter((f) => f.status === "error").length;

    React.useEffect(() => {
        onFilesChange?.(files.map((f) => f.file));
    }, [files, onFilesChange]);

    const validateFile = (file: File): string | null => {
        const isValidType =
            ACCEPTED_TYPES.includes(file.type) ||
            ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

        if (!isValidType) {
            return "Invalid file type";
        }

        if (file.size > maxFileSize) {
            return `Exceeds ${(maxFileSize / 1024 / 1024).toFixed(0)}MB limit`;
        }

        return null;
    };

    const addFiles = React.useCallback(
        (fileList: FileList | null) => {
            if (!fileList || disabled) return;

            if (files.length + fileList.length > maxFiles) {
                return;
            }

            const newFiles: UploadFile[] = [];

            Array.from(fileList).forEach((file) => {
                const error = validateFile(file);
                const isDuplicate = files.some((f) => f.file.name === file.name);

                if (!error && !isDuplicate) {
                    newFiles.push({
                        id: generateId(),
                        file,
                        progress: 0,
                        status: "pending",
                    });
                }
            });

            if (newFiles.length > 0) {
                setFiles((prev) => [...prev, ...newFiles]);
                setUploadStatus("idle");
            }
        },
        [files, maxFiles, disabled, setFiles]
    );

    const handleDrag = React.useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;

            if (e.type === "dragenter" || e.type === "dragover") {
                setDragActive(true);
            } else if (e.type === "dragleave") {
                setDragActive(false);
            }
        },
        [disabled]
    );

    const handleDrop = React.useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
            if (disabled) return;
            addFiles(e.dataTransfer.files);
        },
        [addFiles, disabled]
    );

    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            addFiles(e.target.files);
            e.target.value = "";
        },
        [addFiles]
    );

    const removeFile = React.useCallback((id: string) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    }, [setFiles]);

    // --- INTERNAL UPLOAD LOGIC (Used only when NOT controlled by parent) ---
    const uploadSingleFile = async (
        uploadFile: UploadFile
    ): Promise<UploadResult> => {
        const formData = new FormData();
        formData.append("file", uploadFile.file);
        if (userType) {
            formData.append("userType", userType);
        }

        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setFiles((prev) =>
                        prev.map((f) =>
                            f.id === uploadFile.id
                                ? { ...f, progress, status: "uploading" }
                                : f
                        )
                    );
                }
            });

            xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    setFiles((prev) =>
                        prev.map((f) =>
                            f.id === uploadFile.id
                                ? { ...f, progress: 100, status: "success", uploadedAt: new Date() }
                                : f
                        )
                    );
                    
                    let data = null;
                    try { data = JSON.parse(xhr.responseText); } catch {}
                    resolve({ fileName: uploadFile.file.name, success: true, data });
                } else {
                    let errorMessage = "Upload failed";
                    try {
                        const data = JSON.parse(xhr.responseText);
                        errorMessage = data.message || data.error || errorMessage;
                    } catch { }

                    setFiles((prev) =>
                        prev.map((f) =>
                            f.id === uploadFile.id
                                ? { ...f, status: "error", errorMessage }
                                : f
                        )
                    );
                    resolve({ fileName: uploadFile.file.name, success: false, message: errorMessage });
                }
            });

            xhr.addEventListener("error", () => {
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === uploadFile.id
                            ? { ...f, status: "error", errorMessage: "Network error" }
                            : f
                    )
                );
                resolve({ fileName: uploadFile.file.name, success: false, message: "Network error" });
            });

            xhr.open("POST", uploadUrl);
            xhr.send(formData);
        });
    };

    const retryFile = React.useCallback(
        async (id: string) => {
            const file = files.find((f) => f.id === id);
            if (!file) return;

            setFiles((prev) =>
                prev.map((f) =>
                    f.id === id
                        ? { ...f, status: "pending", progress: 0, errorMessage: undefined }
                        : f
                )
            );

            // If parent controlled, we can't retry individually easily without parent logic.
            // For now, assuming internal retry logic works for simple cases.
            setInternalIsUploading(true);
            await uploadSingleFile({ ...file, status: "pending", progress: 0 });
            setInternalIsUploading(false);
        },
        [files, userType, setFiles]
    );

    const uploadAllFiles = async () => {
        const pendingFiles = files.filter((f) => f.status === "pending");
        if (pendingFiles.length === 0) return;

        setInternalIsUploading(true);
        setUploadStatus("uploading");

        const results = await Promise.all(pendingFiles.map(uploadSingleFile));

        setInternalIsUploading(false);

        const newSuccessCount = results.filter((r) => r.success).length;
        const newErrorCount = results.filter((r) => !r.success).length;

        if (newErrorCount === 0) {
            setUploadStatus("completed");
        } else if (newSuccessCount > 0) {
            setUploadStatus("partial");
        } else {
            setUploadStatus("error");
        }

        onUploadComplete?.(results);
    };

    const clearAll = () => {
        setFiles([]);
        setUploadStatus("idle");
    };

    const clearCompleted = () => {
        setFiles((prev) => prev.filter((f) => f.status !== "success"));
        if (errorCount === 0) {
            setUploadStatus("idle");
        }
    };

    const dismissStatus = () => {
        setUploadStatus("idle");
    };

    return (
        <div className={cn("w-full mx-auto space-y-4", className)}>
            {/* Title for Split View */}
            {title && (
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">{title}</span>
                    <span className="ml-auto text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                        {files.length}
                    </span>
                </div>
            )}

            <AnimatePresence>
                {uploadStatus !== "idle" && (
                    <StatusBanner
                        status={uploadStatus}
                        successCount={successCount}
                        errorCount={errorCount}
                        totalCount={files.length}
                        onDismiss={uploadStatus !== "uploading" ? dismissStatus : undefined}
                    />
                )}
            </AnimatePresence>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-white overflow-hidden p-4 rounded-xl shadow-sm border border-gray-100"
            >
                {/* Header (Clear Buttons) */}
                {files.length > 0 && (
                    <div className="flex justify-end gap-2 mb-4">
                        {successCount > 0 && (
                            <button
                                onClick={clearCompleted}
                                disabled={isUploading}
                                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Clear done
                            </button>
                        )}
                        <button
                            onClick={clearAll}
                            disabled={isUploading}
                            className="text-xs text-red-500 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                            <Trash2 className="h-3 w-3" />
                            Clear all
                        </button>
                    </div>
                )}

                {/* Drop Zone */}
                <div className="mb-4">
                    <div
                        className={cn(
                            "relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 cursor-pointer",
                            "bg-gradient-to-b from-gray-50/50 to-white",
                            dragActive
                                ? "border-emerald-400 bg-emerald-50/50 scale-[1.02] shadow-sm shadow-emerald-100"
                                : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30",
                            (disabled || isUploading) && "pointer-events-none opacity-60"
                        )}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => inputRef.current?.click()}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            onChange={handleChange}
                            multiple
                            accept=".xlsx,.xls,.csv"
                            disabled={disabled || isUploading}
                        />

                        <div className="flex flex-col items-center justify-center gap-3">
                            <motion.div
                                animate={{ scale: dragActive ? 1.1 : 1 }}
                                className={cn(
                                    "p-3 rounded-full transition-all duration-300",
                                    dragActive ? "bg-emerald-100" : "bg-gray-100"
                                )}
                            >
                                <CloudUpload className={cn("h-6 w-6", dragActive ? "text-emerald-600" : "text-gray-400")} />
                            </motion.div>

                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-700">
                                    {dragActive ? "Release to upload!" : "Drag & Drop or Browse"}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    .xlsx, .csv (Max {(maxFileSize / 1024 / 1024).toFixed(0)}MB)
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* File List */}
                <AnimatePresence>
                    {files.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="mb-4"
                        >
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                                <AnimatePresence mode="popLayout">
                                    {files.map((file) => (
                                        <FileItem
                                            key={file.id}
                                            file={file}
                                            onRemove={() => removeFile(file.id)}
                                            onRetry={() => retryFile(file.id)}
                                            disabled={isUploading}
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Upload Button (Internal) */}
                {/* Only show if NOT hidden by parent (hideUploadButton) AND has pending files */}
                {!hideUploadButton && pendingCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                    >
                        <button
                            onClick={uploadAllFiles}
                            disabled={isUploading || disabled}
                            className={cn(
                                "w-full py-3 px-4 rounded-xl font-semibold text-sm text-white transition-all duration-300",
                                "bg-emerald-500 hover:bg-emerald-600",
                                "shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed",
                                "flex items-center justify-center gap-2"
                            )}
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4" />
                                    <span>Upload {pendingCount} file{pendingCount !== 1 ? "s" : ""}</span>
                                    <span className="ml-1 text-xs opacity-80 px-1.5 py-0.5 bg-white/20 rounded">
                                        {formatFileSize(
                                            files
                                                .filter((f) => f.status === "pending")
                                                .reduce((acc, f) => acc + f.file.size, 0)
                                        )}
                                    </span>
                                </>
                            )}
                        </button>
                    </motion.div>
                )}

                {/* Empty State */}
                {files.length === 0 && (
                    <div className="flex items-center justify-center gap-2 py-2 text-gray-400">
                        <FolderOpen className="h-4 w-4" />
                        <span className="text-xs">No files selected</span>
                    </div>
                )}
            </motion.div>
        </div>
    );
};